import React from "react";

import Box from "../Box";
import { Choice, SecRayonType } from "../../types/commercial";
import DeltaBadge from "../DeltaBadge";
import styled from "styled-components";
import MrclText from "../marcel/MrclText";
import useBreakPoint from "../../utils/useBreakPoint";
import BaseDashboardCard from "../BaseDashboardCard";
import SelectOption from "../SelectOption";
import { SingleValue } from "react-select";
import { useSearchParams } from "react-router-dom";
import { Store } from "../../types/store";
import { computeRHRate } from "../../utils/turnover";
import { eventTrack } from "../../utils/gtag";
import { Range } from "../../utils/range";
import { useTranslation } from "react-i18next";
import { useSectorOptions } from "../../utils/sectorsExtractor";
import { formatKvalueCASector } from "../../utils/numberUtils";
import { TFunction } from "i18next";

type SectorRadiusCardProps = {
  range?: Range;
  listValue: Array<{ item: Array<SecRayonType> }>;
  services?: SecRayonType;
  isUnavailable: boolean;
  isLoading: boolean;
  storeType: Store["storeType"];
  openInformationModal: (title?: string) => void;
};

const Text = MrclText;
const Table = styled.table`
  width: 100%;
  align-items: center;
`;
const Tr = styled.tr``;
const Th = styled.th<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;
const BoxCustum = styled(Box)`
  width: 80%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;


const SectorRadiusCard = ({
  range,
  listValue,
  storeType,
  ...res
}: SectorRadiusCardProps) => {
  const isBreak = useBreakPoint();
  const [searchParams, setSearchParams] = useSearchParams();

  let options = useSectorOptions(listValue, "num");

  const sector = searchParams.get("sector") ?? options?.[0]?.value;

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === sector) {
      return;
    }
    searchParams.set("sector", newValue as string);
    setSearchParams(searchParams);
    // tracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: `${sector === "0" ? t('commercial.chiffre_affaires_par_secteur') : t('commercial.chiffre_affaires_par_rayon')}`,
      key2: "filter_name",
      value2: newValue,
    });
  };
  const currentData = listValue?.[Number(sector) || 0]?.item || [];

  const Total =
    currentData?.length > 0
      ? currentData.reduce(
          (a: SecRayonType, b: SecRayonType) => {
            return {
              hypDepartmentKey: "",
              hypDepartmentDesc: "",
              sector: "",
              ca: (a.ca || 0) + (b.ca || 0),
              eb: (a.eb || 0) + (b.hypDepartmentKey?.includes("CADEAUX") ? 0 : b.eb || 0),
              rh: (a.rh || 0) + (b.rh || 0),
              lastCa: (a.lastCa || 0) + (b.lastCa || 0),
            };
          },
          {
            hypDepartmentKey: "",
            hypDepartmentDesc: "",
            sector: "",
            ca: 0,
            eb: 0,
            rh: 0,
            lastCa: 0,
          } as SecRayonType
        )
      : {
          hypDepartmentKey: "",
          hypDepartmentDesc: "",
          sector: "",
          ca: 0,
          eb: 0,
          rh: 0,
          lastCa: 0,
        };
  const TotalRH = computeRHRate(Total?.ca, Total?.lastCa) as number;

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const formatSector = (sector: SecRayonType, t: TFunction) => {
    if (sector?.hypDepartmentDesc?.toUpperCase() === "NON AL") return t('commercial.non_al');
    return currentLanguage === "nl" ? (sector.hypDepartmentDescNl || sector.hypDepartmentDesc) : (sector.hypDepartmentDescFr || sector.hypDepartmentDesc);
  };
  const sectorWidth = "40%" ;
  const otherWidth =  "20%";

  return (
    <BaseDashboardCard
      hasModal={true}
      {...res}
      title={`${sector === "0" ? t('commercial.chiffre_affaires_par_secteur') : t('commercial.chiffre_affaires_par_rayon')}`}
      subTitle={t('commercial.hors_ecommerce')}
      importedDateTime={currentData?.[0]?.importedDateTime}
      option={
        <BoxCustum py={1}>
          { options?.length > 1 && (
            <SelectOption
              list={options}
              value={sector}
              handleChange={handleChange}
            />
          )}
        </BoxCustum>
      }
      openInformationModal={() => null}
      size={"big"}
      width={"100%"}
    >
      <Box
        bg={"white"}
        alignItems="center"
        justifyContent={"center"}
        width="100%"
        borderRadius={1}
        pt={1}
      >
        <Table>
          <thead>
            <Tr>
              <Th style= {{width:sectorWidth}} ></Th>
              <Th style= {{width:otherWidth}} >
                <Text bold size={"s"} tagStyle={"p"}>
                  {t('commercial.ca')}
                </Text>
              </Th>
              <Th $textAlign={"center"}style= {{width:otherWidth}} >
                {isBreak === "phone" ? (
                  <>
                    {" "}
                    <Text tagStyle={"p"} bold size={"s"}>
                      {t('commercial.ecart')}
                    </Text>
                    <Text tagStyle={"p"} bold size={"s"}>
                      {t('commercial.budget')}
                    </Text>
                  </>
                ) : (
                  <Text bold size={"s"} tagStyle={"p"}>
                    {t('commercial.ecart_budget')}
                  </Text>
                )}
              </Th>
              <Th $textAlign="center" style= {{width:otherWidth}} >
                {isBreak === "phone" ? (
                  <>
                    <Text tagStyle={"p"} bold size={"s"}>
                      {"vs"}
                    </Text>
                    <Text tagStyle={"p"} bold size={"s"}>
                      {t('commercial.n_1')}
                    </Text>{" "}
                  </>
                ) : (
                  <Text bold size={"s"} tagStyle={"p"}>
                    {"vs "} {t('commercial.n_1')}
                  </Text>
                )}
              </Th>
            </Tr>
          </thead>
          <tbody>
            {currentData &&
              currentData?.length > 0 &&
              currentData.map((secRayon, index) => {
                return (
                  <Tr key={"sec" + secRayon.hypDepartmentKey}  style= {{width:sectorWidth}}>
                    <Td $textAlign={"left"}>
                      <Text>{formatSector(secRayon, t)}</Text>
                    </Td>
                    <Td  style= {{width:otherWidth}}>
                      <Text>
                        {secRayon.ca
                          ? `${
                              formatKvalueCASector(secRayon.ca)
                            }`
                          : "-"}
                      </Text>
                    </Td>
                    <Td $textAlign={"center"}  style= {{width:otherWidth}}>
                      {secRayon.eb === 0 ? (
                        "-"
                      ) : (
                        <DeltaBadge
                          value={secRayon.eb}
                          formatter={(value) => formatKvalueCASector(value)}
                        />
                      )}
                    </Td>
                    <Td  style= {{width:otherWidth}}>
                      {secRayon.rh &&
                      !isNaN(secRayon.rh) &&
                      isFinite(secRayon.rh) ? (
                       <DeltaBadge value={secRayon.rh} />
                      ) : (
                        "-"
                      )}
                    </Td>
                  </Tr>
                );
              })}
            <Tr>
              <Td $textAlign={"left"} width={"20%"}>
                <Text bold size={"s"} tagStyle={"h4"}>
                  {t('operationnel.total')}
                </Text>
              </Td>
              <Td width={"25%"}>
                <Text>
                    {Total.ca
                      ? `${formatKvalueCASector(Total.ca)}`
                      : "-"}
                </Text>
              </Td>
              <Td $textAlign={"center"}>
                {Total.eb === 0 ? (
                  "-"
                ) : (
                  <DeltaBadge
                    value={Total.eb}
                    formatter={(value) => formatKvalueCASector(value)}
                  />
                )}
              </Td>
              <Td>
                {TotalRH && !isNaN(TotalRH) && isFinite(TotalRH) ? (
                    <DeltaBadge value={TotalRH} />
                ) : (
                  "-"
                )}
              </Td>
            </Tr>
          </tbody>
        </Table>
      </Box>
    </BaseDashboardCard>
  );
};

export default SectorRadiusCard;
